import type { UseFetchOptions } from 'nuxt/app'
import type { ComputedRef } from 'vue'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'

export function useStrapi<T>({
  urlSuffix,
  options = {},
  lazy = false,
}: {
  urlSuffix: Ref<string> | ComputedRef<string> | string
  options?: UseFetchOptions<StrapiApiDefaultResponse<T[]>, T[]>
  lazy?: boolean
}) {
  const config = useRuntimeConfig()
  const preview = useRoute().query.preview === 'true'
  const { strapiApiBaseURL, strapiDraftBaseURL, strapiApiKey } = config.public
  const baseURL = preview ? strapiDraftBaseURL : strapiApiBaseURL
  const urlRef = new URL(baseURL + unref(urlSuffix).replace(/^\/?(api\/)?/, ''))

  if (preview) {
    urlRef.searchParams.set('status', 'draft')
  }

  let key = urlRef.pathname + urlRef.search
  if (key[0] === '/') {
    // remove the leading slash so that google does not crawl unwanted urls
    key = key.slice(1)
  }

  const defaults: UseFetchOptions<StrapiApiDefaultResponse<T[]>, T[]> = {
    baseURL: urlRef.origin,
    key,

    headers: {
      Authorization: `Bearer ${strapiApiKey}`,
    },
    transform: (data) => {
      return data.data
    },
  }

  const params = { ...defaults, ...options }

  return lazy
    ? useLazyFetch(urlRef.href, params)
    : useFetch(urlRef.href, params)
}
